import { AlertTriangle } from 'lucide-react';
import { Formik, Form } from 'formik';
import { object, string } from 'yup';
import { Button } from './CustomButton';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@gokrew/ui/context';
import { FormField } from '@gokrew/ui/form';
import type { DialogComponentProps } from '@/types/dialog';
import { TranslationNS } from '@/constants/translation-namespaces';

type ConfirmationDialogProps = Omit<DialogComponentProps, 'onSubmit'> & {
  onSubmit?: (note?: string) => void;
  confirmDisabled?: boolean;
  variant?: 'default' | 'destructive' | 'warning';
  confirmLabel?: string;
  cancelLabel?: string;
  bullets?: string[];
  noteLabel?: string;
  notePlaceholder?: string;
  noteRequired?: boolean;
  noteRequiredMessage?: string;
};

/**
 * A confirmation dialog component that provides a cancel and confirm button.
 *
 * @param {ConfirmationDialogProps} props - The props for the confirmation dialog component.
 * @param {string} props.dialogId - The id of the dialog to close.
 * @param {Function} props.onSubmit - The function to call when the confirm button is clicked.
 * @param {Function} props.onClose - The function to call when the cancel button is clicked.
 * @param {React.ReactNode} props.children - The children of the confirmation dialog component.
 *
 * @returns The confirmation dialog component.
 */
export function ConfirmationDialog({
  dialogId,
  onSubmit,
  onClose,
  children,
  confirmDisabled,
  variant = 'default',
  confirmLabel,
  cancelLabel,
  bullets,
  noteLabel,
  notePlaceholder,
  noteRequired,
  noteRequiredMessage,
}: ConfirmationDialogProps) {
  const { t } = useTranslation(TranslationNS.LibsCommon);
  const { closeDialog, closeAll } = useDialog();
  const isDestructive = variant === 'destructive';
  const isWarning = variant === 'warning';
  const buttonLabel = confirmLabel ?? t('buttons.confirm');
  const cancelButtonLabel = cancelLabel ?? t('buttons.cancel');
  const hasNote = Boolean(noteLabel);

  /**
   * Closes the dialog with the given dialogId.
   * This function is used to close the dialog after a successful submission or cancellation.
   */
  const closeDialogHandler = () => {
    try {
      if (dialogId) {
        closeDialog(dialogId);
      } else {
        closeAll();
      }
    } catch (e) {
      console.error(e);
      toast(t('error.something_went_wrong'));
    }
  };

  /**
   * Cancels the form submission and closes the dialog.
   * It calls the closeDialogHandler function to close the dialog with the given dialogId,
   * and if onClose is provided, it calls the onClose function to notify the parent component
   * that the dialog has been closed.
   */
  const cancelHandler = () => {
    try {
      closeDialogHandler();
      if (onClose) onClose();
    } catch (e) {
      console.error(e);
      toast(t('error.something_went_wrong'));
    }
  };

  /**
   * Confirms the form submission and closes the dialog.
   * It calls the onSubmit function if it is provided, and then calls the closeDialogHandler function
   * to close the dialog with the given dialogId.
   */
  const validationSchema = noteRequired
    ? object({ note: string().trim().required(noteRequiredMessage) })
    : undefined;

  return (
    <Formik
      initialValues={{ note: '' }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        try {
          if (onSubmit) onSubmit(hasNote ? values.note : undefined);
          closeDialogHandler();
        } catch (e) {
          console.error(e);
          toast(t('error.something_went_wrong'));
        }
      }}
    >
      <Form className="space-y-4">
        {bullets && bullets.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-800">
              {t('confirmation_dialog.assess_consequences')}
            </p>
            <ul className="space-y-2 pl-1">
              {bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-500" />
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-sm text-gray-600">{children}</div>
        )}

        {hasNote && (
          <FormField
            name="note"
            label={noteLabel!}
            type="textarea"
            placeholder={notePlaceholder}
          />
        )}

        {isDestructive && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-100 bg-red-50 p-3">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-500" />
            <p className="text-sm leading-relaxed text-gray-800">
              {t('confirmation_dialog.are_you_sure')}{' '}
              <span className="font-semibold text-red-600">
                {t('confirmation_dialog.cannot_be_undone')}
              </span>
            </p>
          </div>
        )}

        <div className="flex items-center gap-2 justify-end pt-1">
          <Button variant="outline" type="button" onClick={cancelHandler}>
            {cancelButtonLabel}
          </Button>
          <Button
            variant={isDestructive || isWarning ? 'destructive' : 'primary'}
            type="submit"
            disabled={confirmDisabled}
            className={confirmDisabled ? 'opacity-50 cursor-not-allowed' : ''}
          >
            {buttonLabel}
          </Button>
        </div>
      </Form>
    </Formik>
  );
}
